// Navigation state helpers (UX-01..04, UX-38): URL-backed view state, page titles + route announcements,
// scroll restoration and focus management, in-app history depth for "Voltar".
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";

const SITE = "Planos de Gestão";
const DEPTH_KEY = "nav-depth";
const SCROLL_KEY = "nav-scroll";

let poppedAt = 0;
if (typeof window !== "undefined") {
  window.history.scrollRestoration = "manual";
  window.addEventListener("popstate", () => {
    poppedAt = Date.now();
    bumpDepth(-1);
  });
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function bumpDepth(delta: number) {
  const s = storage();
  if (!s) return;
  const n = Math.max(0, Number(s.getItem(DEPTH_KEY) ?? "0") + delta);
  s.setItem(DEPTH_KEY, String(n));
}

/** True when there is an in-app page behind the current one in this tab's history. */
export const hasInAppHistory = () => Number(storage()?.getItem(DEPTH_KEY) ?? "0") > 0;

function readScroll(): Record<string, number> {
  try {
    return JSON.parse(storage()?.getItem(SCROLL_KEY) ?? "{}") as Record<string, number>;
  } catch {
    return {};
  }
}
function writeScroll(key: string, y: number) {
  const all = readScroll();
  all[key] = y;
  const keys = Object.keys(all);
  if (keys.length > 100) delete all[keys[0]!];
  try {
    storage()?.setItem(SCROLL_KEY, JSON.stringify(all));
  } catch {
    /* storage full or blocked: scroll memory is a convenience only */
  }
}

/**
 * Mounted once. On forward navigation: scroll to top, move focus to the page heading, count depth.
 * On back/forward: restore the saved scroll once the page has enough height (data may still be loading).
 */
export function RouteEffects() {
  const [path] = useLocation();
  const search = useSearch();
  const key = `${path}?${search}`;
  const firstRender = useRef(true);
  const lastPath = useRef(path);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => writeScroll(key, window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, [key]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const pathChanged = lastPath.current !== path;
    lastPath.current = path;
    const isPop = Date.now() - poppedAt < 400;
    if (isPop) {
      const target = readScroll()[key] ?? 0;
      let tries = 0;
      const restore = () => {
        const reachable = document.documentElement.scrollHeight - window.innerHeight >= target - 2;
        if (reachable || tries > 40) window.scrollTo(0, target);
        else {
          tries++;
          setTimeout(restore, 50);
        }
      };
      restore();
      return;
    }
    // Search-only changes (filters written to the URL with replace) keep the position.
    if (!pathChanged) return;
    bumpDepth(1);
    window.scrollTo(0, 0);
    const t = setTimeout(() => document.getElementById("page-title")?.focus({ preventScroll: true }), 60);
    return () => clearTimeout(t);
  }, [key, path]);

  return null;
}

/** Sets the tab title and announces the page to screen readers (UX-04, UX-38). */
export function usePageTitle(...parts: (string | null | undefined)[]) {
  const title = [...parts.filter(Boolean), SITE].join(" · ");
  useEffect(() => {
    document.title = title;
    const live = document.getElementById("route-announcer");
    if (live) live.textContent = parts.filter(Boolean)[0] ?? SITE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);
}

export function RouteAnnouncer() {
  return <div id="route-announcer" aria-live="polite" aria-atomic="true" className="sr-only" />;
}

/**
 * View state mirrored in the URL query (UX-02): filters, tabs and pages survive back, reload and sharing.
 * Values equal to their default are omitted; updates replace the history entry instead of stacking, unless
 * `push` is set (drill-down levels that browser Back should step through).
 */
export function useUrlState<T extends Record<string, string>>(defaults: T): [T, (patch: Partial<T>, opts?: { push?: boolean }) => void] {
  const search = useSearch();
  const [path, navigate] = useLocation();
  const state = useMemo(() => {
    const q = new URLSearchParams(search);
    const out = { ...defaults };
    for (const k of Object.keys(defaults) as (keyof T)[]) {
      const v = q.get(k as string);
      if (v !== null) out[k] = v as T[keyof T];
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const set = useCallback(
    (patch: Partial<T>, opts?: { push?: boolean }) => {
      const q = new URLSearchParams(search);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === "" || v === defaults[k]) q.delete(k);
        else q.set(k, v as string);
      }
      const qs = q.toString();
      navigate(qs ? `${path}?${qs}` : path, { replace: !opts?.push });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, path, navigate],
  );
  return [state, set];
}

const LAST_PLAN = "last-plan";
export const rememberPlan = (id: string) => {
  try {
    window.localStorage.setItem(LAST_PLAN, id);
  } catch {
    /* convenience only */
  }
};
export const lastPlan = () => {
  try {
    return window.localStorage.getItem(LAST_PLAN);
  } catch {
    return null;
  }
};

/** Per-viewer UI preference in localStorage (never content, drafts or permissions). */
export function usePreference<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(`pref:${key}`);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (v: T) => {
      setValue(v);
      try {
        window.localStorage.setItem(`pref:${key}`, JSON.stringify(v));
      } catch {
        /* convenience only */
      }
    },
    [key],
  );
  return [value, set];
}
